import {
  checkApkManifest,
  HIDDEN_UPDATE,
  readLastApkCheck,
  reduceUpdate,
  sha256Matches,
  writeLastApkCheck,
  type ApkManifest,
  type UpdateBarState,
  type UpdateEvent,
  APK_MANIFEST_URL,
} from "./apkUpdate";
import { isNativePlatform, readNativeVersionCode } from "./nativeShell";

type Listener = () => void;

let state: UpdateBarState = { ...HIDDEN_UPDATE };
const listeners = new Set<Listener>();

export type ApkDownloader = (
  manifest: ApkManifest,
  onProgress: (received: number, total: number | null) => void,
) => Promise<{ sha256: string }>;

async function nativeDownload(
  manifest: ApkManifest,
  onProgress: (received: number, total: number | null) => void,
): Promise<{ sha256: string }> {
  const { registerPlugin } = await import("@capacitor/core");
  const shell = registerPlugin<{
    downloadApk(options: { url: string; sha256: string }): Promise<{ sha256: string }>;
    addListener(
      event: "progress",
      cb: (data: { received?: number; total?: number }) => void,
    ): Promise<{ remove: () => Promise<void> }>;
  }>("NorthstarShell");
  const handle = await shell.addListener("progress", (data) => {
    onProgress(Number(data.received) || 0, data.total != null ? Number(data.total) : null);
  });
  try {
    return await shell.downloadApk({ url: manifest.url, sha256: manifest.sha256 });
  } finally {
    await handle.remove();
  }
}

let downloader: ApkDownloader = nativeDownload;

export function setApkDownloaderForTests(next: ApkDownloader | null): void {
  downloader = next ?? nativeDownload;
}

export function getApkSession(): UpdateBarState {
  return state;
}

export function resetApkSession(): void {
  state = { ...HIDDEN_UPDATE };
  listeners.forEach((listener) => listener());
}

function emit(event: UpdateEvent): void {
  state = reduceUpdate(state, event);
  listeners.forEach((listener) => listener());
}

export function subscribeApkSession(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export async function refreshApkOffer(input: { manual: boolean; storage?: Storage | null; now?: number }): Promise<string> {
  if (state.phase === "downloading" || state.phase === "ready") {
    return input.manual ? "A download is already on this phone." : "";
  }
  const now = input.now ?? Date.now();
  const online = typeof navigator === "undefined" ? true : navigator.onLine !== false;
  let localCode: number | null = null;
  if (isNativePlatform()) {
    try {
      localCode = await readNativeVersionCode();
    } catch {
      if (input.manual) return "Could not read this phone's app version.";
    }
  }
  const result = await checkApkManifest({
    manual: input.manual,
    online,
    now,
    lastCheckAt: readLastApkCheck(input.storage),
    localCode,
    fetchManifest: async () => {
      const response = await fetch(APK_MANIFEST_URL, { cache: "no-store" });
      if (!response.ok) throw new Error(String(response.status));
      return response.json();
    },
  });
  if (result.checked) writeLastApkCheck(now, input.storage);
  if (result.decision === "available" && result.manifest) emit({ type: "offer", manifest: result.manifest });
  return result.message;
}

export async function startApkDownload(): Promise<void> {
  const manifest: ApkManifest | null =
    state.url && state.sha256
      ? {
          versionCode: state.versionCode,
          versionName: state.versionName,
          notes: state.notes,
          url: state.url,
          sha256: state.sha256,
        }
      : null;
  if (!manifest) return;
  if (state.phase === "downloading") return;
  emit({ type: "download" });
  try {
    const result = await downloader(manifest, (received, total) => emit({ type: "progress", received, total }));
    if (!sha256Matches(result.sha256, manifest.sha256)) {
      emit({ type: "fail", error: "Update file did not match the published checksum." });
      return;
    }
    emit({ type: "verified" });
  } catch (error) {
    const message = error instanceof Error && error.message ? error.message : "Download failed";
    emit({ type: "fail", error: message });
  }
}

export async function installDownloadedApk(): Promise<void> {
  if (state.phase !== "ready" && state.phase !== "error") return;
  try {
    const { registerPlugin } = await import("@capacitor/core");
    const shell = registerPlugin<{ installApk(): Promise<void> }>("NorthstarShell");
    await shell.installApk();
  } catch (error) {
    const message = error instanceof Error && error.message ? error.message : "Could not open the installer";
    emit({ type: "install-error", error: message });
  }
}

export function primeApkOffer(manifest: ApkManifest): void {
  emit({ type: "offer", manifest });
}
